<template>
    <v-row>
      <v-col cols="12">
        <base-card title="Filter" icon="mdi-filter-outline">
            <v-form @submit.prevent="loadDashboard">
              <v-card-text class="pa-2">
                  <v-row dense>
                    <v-col cols="12" sm="6" md="4" lg="2">
                      <v-select
                        v-model="period"
                        bg-color="grey-lighten-4"
                        :menu-props="{ contentClass: 'my-custom-dropdown' }"
                        :items="periodOptions"
                        label="Period"
                        v-bind="inputProps"
                        @update:model-value="applyPeriod"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4" lg="2">
                      <v-date-input
                        v-model="filters.from_date"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        label="From"
                        v-bind="inputProps"
                        @update:model-value="period = 'custom'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4" lg="2">
                      <v-date-input
                        v-model="filters.to_date"
                        prepend-icon=""
                        bg-color="grey-lighten-4"
                        label="To"
                        v-bind="inputProps" @update:model-value="period = 'custom'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4" lg="2">
                      <v-select
                        v-model="filters.employee_id"
                        :items="employees"
                        item-title="label"
                        item-value="id"
                        bg-color="grey-lighten-4"
                        :menu-props="{ contentClass: 'my-custom-dropdown' }"
                        label="Salesperson"
                        v-bind="inputProps"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4" lg="2">
                      <v-select
                        v-model="filters.currency_id"
                        :items="currencies"
                        bg-color="grey-lighten-4"
                        :menu-props="{ contentClass: 'my-custom-dropdown' }"
                        item-title="code"
                        item-value="id"
                        label="Currency"
                        v-bind="inputProps"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4" lg="2">
                      <v-select
                        v-model="filters.compare"
                        :items="compareOptions"
                        label="Compare with"
                        v-bind="inputProps"
                        bg-color="grey-lighten-4"
                        :menu-props="{ contentClass: 'my-custom-dropdown' }"
                      />
                    </v-col>
                  </v-row>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions class="d-flex justify-end">
                <v-btn type="submit" color="primary" variant="flat">
                  Apply
                </v-btn>
              </v-card-actions>
            </v-form>
        </base-card>
      </v-col>
    </v-row>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >{{ error }}</v-alert>


    <template v-if="dashboard">

      <v-row class="mt-4" dense>
          <v-col v-for="card in cards" :key="card.key" cols="12" md="3">
            <v-card
              variant="elevated"
              class="px-3 pt-3 h-100"
              :class="!card.disabled ? 'cursor-pointer': 'cursor-default'"
              type="button"
              @click="openCard(card.key)"
            >
              <div class="d-flex align-center justify-space-between">
                <v-icon :icon="card.icon" size="small" />
                <span class="text-body-2 ml-3">{{ card.label }}</span>
              </div>
              <div class="d-flex justify-center mt-3">
                <v-chip
                    variant="tonal"
                    color="primary"
                    class="text-subtitle-3 font-weight-bold"
                >{{ card.value }}</v-chip>
              </div>
              <v-card-actions style="min-height: 10px" class="d-flex justify-end">
                <v-icon
                    v-if="!card.disabled"
                    icon="mdi-arrow-top-right"
                    size="x-small"
                    color="primary"
                />
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

      <v-tabs
        v-model="activeTab"
        color="primary"
        align-tabs="end"
      >
        <v-tab
          v-for="tab in tabs"
          :key="tab.value"
          :value="tab.value"
          class="text-subtitle-2 font-weight-semibold"
        >
          <v-icon v-if="tab.icon" start :icon="tab.icon" />
          {{ tab.title }}
        </v-tab>
      </v-tabs>

      <v-divider/>
      <v-window v-model="activeTab">
        <!-- 04. Overview: trend, customer ranking and a personal action queue -->
        <v-window-item value="overview">
          <v-row dense>
          <!-- Sales vs target panel -->
          <v-col cols="12" lg="8">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Sales vs target</h3>
                <p class="text-caption text-medium-emphasis mt-1">Approved invoice totals less returns. Target follows the approved monthly allocation.</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <template >
                <!-- Trend sparkline -->
                <div class="d-flex flex-column ga-3">
                  <div class="d-flex flex-wrap ga-4 mb-2">
                    <span class="d-flex align-center ga-2 text-caption text-medium-emphasis">
                      <v-icon icon="mdi-circle" size="10" color="primary" /> Sales
                    </span>
                    <span class="d-flex align-center ga-2 text-caption text-medium-emphasis">
                      <v-icon icon="mdi-circle" size="10" color="secondary" /> Approved target
                    </span>
                  </div>
                  <apexchart
                    type="line"
                    height="300"
                    :options="salesTargetChartOptions"
                    :series="salesTargetChartSeries"
                  />
                </div>
              </template>
            </div>
            </v-card>
          </v-col>

          <!-- Top customers panel -->
          <v-col cols="12" lg="4">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Top customers</h3>
                <p class="text-caption text-medium-emphasis mt-1">Top 5 by net invoiced value in this period</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <div v-if="!dashboard.top_customers.length" class="d-flex flex-column align-center justify-center text-center text-medium-emphasis pa-6">
                <v-icon icon="mdi-account-group-outline" size="34" />
                <p>No accessible invoiced sales in this period.</p>
              </div>
              <div
                v-for="(customer, index) in dashboard.top_customers"
                :key="customer.id"
                class="d-flex align-center ga-3 py-3 border-b"
              >
                <span class="d-flex align-center justify-center bg-blue-lighten-5 text-primary rounded pa-2">{{ index + 1 }}</span>
                <div class="flex-grow-1">
                  <strong>{{ customer.name }}</strong>
                  <small>{{ customer.code }}</small>
                </div>
                <strong class="font-weight-medium">{{ money(customer.amount) }}</strong>
              </div>
            </div>
            </v-card>
          </v-col>

          <!-- Sales & collections panel -->
          <v-col cols="12" lg="8">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Sales &amp; collections</h3>
                <p class="text-caption text-medium-emphasis mt-1">Invoices follow invoice dates; collections follow payment dates and approved receipt vouchers.</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
                <div class="d-flex flex-column ga-3">
                  <div class="d-flex flex-wrap ga-4 mb-2">
                    <span class="d-flex align-center ga-2 text-caption text-medium-emphasis">
                      <v-icon icon="mdi-circle" size="10" color="primary" /> Sales
                    </span>
                    <span class="d-flex align-center ga-2 text-caption text-medium-emphasis">
                      <v-icon icon="mdi-circle" size="10" color="success" /> Posted collections
                    </span>
                  </div>
                  <apexchart
                    type="line"
                    height="300"
                    :options="salesCollectionChartOptions"
                    :series="salesCollectionChartSeries"
                  />
                </div>
            </div>
            </v-card>
          </v-col>

          <!-- Attention required panel -->
          <v-col id="sales-action-center" cols="12" lg="4">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Attention required</h3>
                <p class="text-caption text-medium-emphasis mt-1">Current actions, independent of the selected period</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <v-btn
                class="d-flex align-center ga-3 w-100 py-3 border-b text-left"
                type="button"
                @click="approvalOpen = true"
              >
                <span class="pa-2 rounded-lg bg-purple-lighten-5 text-purple">
                  <v-icon icon="mdi-clipboard-check-outline" />
                </span>
                <span>
                  <strong>My approval requests</strong>
                  <small>Only the current assigned step</small>
                </span>
                <b>{{ dashboard.approvals.length }}</b>
              </v-btn>
              <v-btn
                class="d-flex align-center ga-3 w-100 py-3 border-b text-left"
                type="button"
                @click="activeTab = 'operations'"
              >
                <span class="pa-2 rounded-lg bg-orange-lighten-5 text-orange">
                  <v-icon icon="mdi-truck-alert-outline" />
                </span>
                <span>
                  <strong>Delayed orders</strong>
                  <small>Recorded delivery date has passed</small>
                </span>
                <b>{{ integer(dashboard.orders.late_count) }}</b>
              </v-btn>
              <v-btn
                class="d-flex align-center ga-3 w-100 py-3 border-b text-left"
                type="button"
                @click="openDetails('overdue')"
              >
                <span class="pa-2 rounded-lg bg-red-lighten-5 text-red">
                  <v-icon icon="mdi-clock-alert-outline" />
                </span>
                <span>
                  <strong>Overdue receivables</strong>
                  <small>Current invoice balances</small>
                </span>
                <b>{{ money(dashboard.kpis.overdue) }}</b>
              </v-btn>
              <v-btn
                class="d-flex align-center ga-3 w-100 py-3 border-b text-left"
                type="button"
                @click="activeTab = 'pipeline'"
              >
                <span class="pa-2 rounded-lg bg-blue-lighten-5 text-primary">
                  <v-icon icon="mdi-calendar-check-outline" />
                </span>
                <span>
                  <strong>Follow-ups due</strong>
                  <small>Open the activity list</small>
                </span>
                <v-icon icon="mdi-chevron-right" size="20" />
              </v-btn>
            </div>
            </v-card>
          </v-col>
          </v-row>
        </v-window-item>

        <!-- 05. Employee performance -->
        <v-window-item value="performance">
          <v-card>
          <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
            <div>
              <h3 class="text-subtitle-1 font-weight-bold">Target &amp; employee performance</h3>
              <p class="text-caption text-medium-emphasis mt-1">Approved active employee targets only. Actuals use salesperson/dealing-person mapping, never the entry creator.</p>
            </div>
            <v-chip size="small" variant="tonal" color="primary">
              Targets: {{ dashboard.meta.target_currency || "Not configured" }}
            </v-chip>
          </div>
          <div class="pa-4 pt-2">
            <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-4">
              <v-text-field
                v-model="employeeSearch"
                label="Search employee"
                prepend-inner-icon="mdi-magnify"
                v-bind="inputProps"
                class="flex-grow-1"
              />
              <span class="text-caption text-medium-emphasis">Run rate is per remaining calendar day.</span>
            </div>
            <v-data-table
              :headers="performanceHeaders"
              :items="dashboard.performance"
              :search="employeeSearch"
              :items-per-page="10"
              density="comfortable"
            >
              <template #item.name="{ item }">
                <strong>{{ item.name }}</strong>
                <div class="text-caption text-medium-emphasis">{{ item.code }}</div>
              </template>
              <template #item.target="{ item }">
                {{ money(item.target, dashboard.meta.target_currency) }}
              </template>
              <template #item.actual="{ item }">
                {{ money(item.actual) }}
              </template>
              <template #item.achievement="{ item }">
                <div class="d-flex flex-column ga-1">
                  <span>{{ percent(item.achievement) }}</span>
                  <v-progress-linear
                    v-if="item.achievement !== null"
                    :model-value="Math.min(100, Math.max(0, item.achievement))"
                    color="primary"
                    height="5"
                    rounded
                  />
                </div>
              </template>
              <template #item.remaining="{ item }">
                {{ money(item.remaining) }}
              </template>
              <template #item.run_rate="{ item }">
                {{ money(item.run_rate) }}
                <small v-if="item.run_rate !== null" class="text-caption text-medium-emphasis"> / day</small>
              </template>
              <template #item.position="{ item }">
                <v-chip :color="positionColor(item.position)" size="small" variant="tonal">
                  {{ item.position }}
                </v-chip>
              </template>
              <template #no-data>
                <div class="d-flex flex-column align-center justify-center text-center text-medium-emphasis pa-6">
                  No accessible sales or approved employee targets for this period.
                </div>
              </template>
            </v-data-table>
          </div>
          </v-card>
        </v-window-item>

        <!-- 06. Pipeline and actionable CRM records -->
        <v-window-item value="pipeline">
          <v-row dense>
          <!-- CRM activity summary -->
          <v-col cols="12" lg="4">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">CRM activity summary</h3>
                <p class="text-caption text-medium-emphasis mt-1">Lead and proposal counts follow the selected date range.</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <div class="d-flex align-center justify-space-between ga-3 py-4 border-b">
                <span>New leads</span>
                <strong>{{ integer(dashboard.crm.leads) }}</strong>
              </div>
              <div class="d-flex align-center justify-space-between ga-3 py-4 border-b">
                <span>Proposals created</span>
                <strong>{{ integer(dashboard.crm.proposals) }}</strong>
              </div>
              <div class="d-flex align-center justify-space-between ga-3 py-4 border-b">
                <span>Current open pipeline</span>
                <strong>{{ money(dashboard.crm.pipeline_value) }}</strong>
              </div>
              <p class="text-caption text-medium-emphasis mt-4">
                Pipeline uses Customer Profile opportunities in {{ dashboard.meta.currency }}. It is not
                booked sales.
              </p>
            </div>
            </v-card>
          </v-col>

          <!-- Open opportunity pipeline -->
          <v-col cols="12" lg="8">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Open opportunity pipeline</h3>
                <p class="text-caption text-medium-emphasis mt-1">Current opportunity value by stage; won/lost opportunities are excluded.</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <template v-if="dashboard.crm.pipeline.length">
                <apexchart
                  type="bar"
                  height="300"
                  :options="pipelineChartOptions"
                  :series="pipelineChartSeries"
                />
              </template>
            </div>
            </v-card>
          </v-col>

          <!-- Lead follow-ups -->
          <v-col cols="12" lg="6">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Lead follow-ups</h3>
                <p class="text-caption text-medium-emphasis mt-1">Next 12 due/overdue lead follow-ups, ordered by date</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <v-data-table
                v-if="dashboard.crm.followups.length"
                :headers="followupHeaders"
                :items="dashboard.crm.followups"
                :items-per-page="12"
                density="comfortable"
                hide-default-footer
              >
                <template #item.name="{ item }">
                  <strong>{{ item.name }}</strong>
                  <div class="text-caption text-medium-emphasis">{{ item.code }}</div>
                </template>
                <template #item.due_date="{ item }">{{ date(item.due_date) }}</template>
                <template #item.action="{ item }">
                  <v-btn :to="'/sales/crm-view-lead/' + item.id" size="small" variant="text">Open</v-btn>
                </template>
              </v-data-table>
            </div>
            </v-card>
          </v-col>

          <!-- Customer next actions -->
          <v-col cols="12" lg="6">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Customer next actions</h3>
                <p class="text-caption text-medium-emphasis mt-1">Next 12 open Customer Profile activities due today or earlier</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <v-data-table
                v-if="dashboard.crm.activities.length"
                :headers="activityHeaders"
                :items="dashboard.crm.activities"
                :items-per-page="12"
                density="comfortable"
                hide-default-footer
              >
                <template #item.customer="{ item }">
                  <strong>{{ item.customer }}</strong>
                  <div class="text-caption text-medium-emphasis">{{ item.next_action }}</div>
                </template>
                <template #item.due_date="{ item }">{{ date(item.due_date) }}</template>
                <template #item.action="{ item }">
                  <v-btn
                    :to="{ path: '/customer-profile', query: { customer_id: item.customer_id, tab: 'activity' } }"
                    size="small"
                    variant="text"
                  >Open</v-btn>
                </template>
              </v-data-table>
            </div>
            </v-card>
          </v-col>
          </v-row>
        </v-window-item>

        <!-- 07. Operational snapshots -->
        <v-window-item value="operations">
          <v-row dense>
          <!-- Receivable aging -->
          <v-col cols="12" lg="6">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Receivable aging</h3>
                <p class="text-caption text-medium-emphasis mt-1">
                  Current unpaid invoices as of {{ date(dashboard.meta.as_of) }}; grouped by recorded next payment date.
                </p>
              </div>
            </div>
            <div class="pa-4 pt-2">
                <v-list density="compact">
                  <apexchart
                    type="donut"
                    height="300"
                    :options="agingChartOptions"
                    :series="agingChartSeries"
                  />
                </v-list>
            </div>
            </v-card>
          </v-col>

          <!-- Top overdue customers -->
          <v-col cols="12" lg="6">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Top overdue customers</h3>
                <p class="text-caption text-medium-emphasis mt-1">Top 10 by current overdue invoice balance</p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <div
                v-for="customer in dashboard.receivables.customers"
                :key="customer.id"
                class="d-flex align-center ga-3 py-3 border-b"
              >
                <div class="flex-grow-1">
                  <strong>{{ customer.name }}</strong>
                  <small>Oldest due: {{ date(customer.oldest_due_date) }}</small>
                </div>
                <strong class="font-weight-medium text-error">{{ money(customer.amount) }}</strong>
              </div>
              <div v-if="!dashboard.receivables.customers.length" class="d-flex flex-column align-center justify-center text-center text-medium-emphasis pa-6">
                No accessible overdue balances.
              </div>
            </div>
            </v-card>
          </v-col>

          <!-- Open delivery backlog -->
          <v-col cols="12">
            <v-card class="h-100">
            <div class="d-flex align-start justify-space-between flex-wrap ga-2 pa-4 pb-2">
              <div>
                <h3 class="text-subtitle-1 font-weight-bold">Open delivery backlog</h3>
                <p class="text-caption text-medium-emphasis mt-1">
                  {{ integer(dashboard.orders.count) }} open orders ·
                  {{ integer(dashboard.orders.late_count) }} delayed · next 12 orders shown
                </p>
              </div>
            </div>
            <div class="pa-4 pt-2">
              <v-data-table
                :headers="orderHeaders"
                :items="dashboard.orders.records"
                :items-per-page="12"
                density="comfortable"
                hide-default-footer
              >
                <template #item.due_date="{ item }">
                  <span :class="{ 'text-error': isOverdue(item.due_date) }">
                    {{ date(item.due_date) }}
                  </span>
                </template>
                <template #item.amount="{ item }">
                  {{ money(item.amount) }}
                </template>
                <template #no-data>
                  <div class="d-flex flex-column align-center justify-center text-center text-medium-emphasis pa-6">
                     No pending deliveries in this scope and currency.
                  </div>
                </template>
              </v-data-table>
              <p class="text-caption text-medium-emphasis mt-3">
                Remaining order-line value; header-level tax and discount are not allocated to backlog.
              </p>
            </div>
            </v-card>
          </v-col>
          </v-row>
        </v-window-item>
      </v-window>
    </template>

    <!-- 09. Server-paginated, permission-scoped KPI drill-down -->
    <v-dialog v-model="detailOpen" max-width="880" scrollable>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ detailTitle }}</span>
          <v-btn icon="mdi-close" variant="text" aria-label="Close details" @click="detailOpen = false" />
        </v-card-title>
        <v-card-text>
          <div v-if="['invoices', 'returns'].includes(detailKind)" class="mb-3">
            <v-btn-toggle
              :model-value="detailKind"
              color="primary"
              mandatory
              density="compact"
              @update:model-value="changeDetailKind"
            >
              <v-btn value="invoices">Invoices</v-btn>
              <v-btn value="returns">Returns</v-btn>
            </v-btn-toggle>
            <p class="text-caption text-medium-emphasis mt-2">
              Net invoiced value = invoice totals − approved returns in the same period and
              currency.
            </p>
          </div>
          <v-alert v-if="detailError" type="error" variant="tonal" class="mb-3">
            {{ detailError }}
          </v-alert>
          <v-progress-linear v-if="detailLoading" indeterminate color="primary" />
          <v-data-table
            :headers="detailHeaders"
            :items="detailData?.rows || []"
            :items-per-page="10"
            density="comfortable"
            hide-default-footer
          >
            <template #item.date="{ item }">{{ date(item.date) }}</template>
            <template #item.amount="{ item }">
              <span class="d-block text-end">{{ number(item.amount) }}</span>
            </template>
          </v-data-table>
          <div v-if="!detailLoading && !detailError && !detailData?.rows.length" class="d-flex flex-column align-center justify-center text-center text-medium-emphasis pa-6">
            No matching records.
          </div>
          <div v-if="detailData" class="d-flex align-center justify-space-between flex-wrap ga-2 mt-4">
            <span class="text-caption text-medium-emphasis">{{ detailData.total }} records</span>
            <v-pagination
              v-model="detailPage"
              :length="detailData.pages"
              :total-visible="5"
              density="compact"
              @update:model-value="loadDetails"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 10. Approval dialog -->
    <v-dialog v-model="approvalOpen" max-width="820" scrollable>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>My pending approvals</span>
          <v-btn icon="mdi-close" variant="text" aria-label="Close approvals" @click="approvalOpen = false" />
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="approvalHeaders"
            :items="dashboard?.approvals || []"
            :items-per-page="10"
            density="comfortable"
          >
            <template #item.waiting_since="{ item }">
              {{ date(item.waiting_since) }}
            </template>
            <template #item.action="{ item }">
              <v-btn :to="item.path" color="primary" variant="text" size="small">Review</v-btn>
            </template>
            <template #no-data>
              <div class="d-flex flex-column align-center justify-center text-center text-medium-emphasis pa-6">
                No request is currently waiting for your approval.
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

</template>

<script>
import baseCard from "@/components/utils/baseCard.vue";
import Axios from "axios";

export default {
  components: {
    BaseCard: baseCard,
  },
  data() {
    return {
      apiUrl: (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "") +
        "/auth/sales/get-sale-dashboard-summary",
      dashboard: null,
      loading: false,
      error: "",
      activeTab: "overview",
      tabs: [
        { title: "Overview", value: "overview", icon: "mdi-view-dashboard-outline" },
        { title: "Targets & Team", value: "performance", icon: "mdi-target" },
        { title: "Pipeline & Activities", value: "pipeline", icon: "mdi-filter-variant" },
        { title: "Orders & Collections", value: "operations", icon: "mdi-truck-delivery-outline" },
      ],
      period: "month",
      employeeSearch: "",
      approvalOpen: false,
      filters: { from_date: "", to_date: "", employee_id: null, currency_id: null, compare: "previous" },
      appliedFilters: {},
      inputProps: { density: "compact", variant: "outlined", hideDetails: true },
      periodOptions: [
        { title: "Today", value: "today" }, { title: "This month", value: "month" },
        { title: "This quarter", value: "quarter" }, { title: "This year", value: "year" },
        { title: "Custom", value: "custom" },
      ],
      compareOptions: [
        { title: "Previous equivalent period", value: "previous" },
        { title: "Same period last year", value: "last_year" },
      ],
      requestSequence: 0,
      requestController: null,
      detailSequence: 0,
      detailController: null,
      scopeTimer: null,
      disposed: false,
      detailOpen: false,
      detailKind: "invoices",
      detailPage: 1,
      detailData: null,
      detailLoading: false,
      detailError: "",
      performanceHeaders: [
        { title: "Employee", key: "name" }, { title: "Approved target", key: "target" },
        { title: "Net actual", key: "actual" }, { title: "Achievement", key: "achievement" },
        { title: "Remaining", key: "remaining" }, { title: "Required run rate", key: "run_rate" },
        { title: "Position", key: "position" },
      ],
      orderHeaders: [
        { title: "Order", key: "code" }, { title: "Customer", key: "customer" },
        { title: "Expected delivery", key: "due_date" },
        { title: "Remaining value", key: "amount", align: "end" },
      ],
      approvalHeaders: [
        { title: "Code", key: "code" }, { title: "Type", key: "type" },
        { title: "Waiting since", key: "waiting_since" },
        { title: "Action", key: "action", sortable: false },
      ],
      followupHeaders: [
        { title: "Lead / company", key: "name" },
        { title: "Due", key: "due_date" },
        { title: "Action", key: "action", sortable: false },
      ],
      activityHeaders: [
        { title: "Customer / action", key: "customer" },
        { title: "Due", key: "due_date" },
        { title: "Action", key: "action", sortable: false },
      ],
      detailHeaders: [
        { title: "Code", key: "code" },
        { title: "Customer", key: "customer" },
        { title: "Date", key: "date" },
        { title: "Amount", key: "amount", align: "end" },
      ],
    };
  },
  computed: {
    employees() {
      return (this.dashboard?.filters.employees || []).map((employee) => ({
        ...employee, label: employee.code + " — " + employee.name,
      }));
    },
    currencies() {
      return this.dashboard?.filters.currencies?.length
        ? this.dashboard.filters.currencies : [{ id: 0, code: "BDT" }];
    },
    updatedAt() {
      return this.dashboard ? new Date(this.dashboard.meta.generated_at).toLocaleTimeString("en-GB") : "";
    },
    cards() {
      if (!this.dashboard) return [];
      const k = this.dashboard.kpis;
      return [
    {
      key: "sales",
      label: "Sales",
      value: this.money(k.sales),
      icon: "mdi-chart-line",
      tone: "blue",
    },
    {
      key: "target",
      label: "Approved target",
      value: this.money(k.target, this.dashboard.meta.target_currency),
      icon: "mdi-bullseye-arrow",
      tone: "purple",
    },
    {
      key: "achievement",
      label: "Target achievement",
      value: this.percent(k.achievement),
      icon: "mdi-trophy-outline",
      tone: "green",
    },
    {
      key: "collections",
      label: "Posted collections",
      value: this.money(k.collections),
      icon: "mdi-cash-check",
      tone: "green",
    },
    {
      key: "outstanding",
      label: "Outstanding receivables",
      value: this.money(k.outstanding),
      icon: "mdi-wallet-outline",
      tone: "blue",
    },
    {
      key: "overdue",
      label: "Overdue receivables",
      value: this.money(k.overdue),
      icon: "mdi-clock-alert-outline",
      tone: "red",
    },
    {
      key: "backlog",
      label: "Open delivery backlog",
      value: this.money(k.backlog),
      icon: "mdi-truck-delivery-outline",
      tone: "amber",
    },
    {
      key: "approvals",
      label: "My pending approvals",
      value: this.integer(k.approvals),
      icon: "mdi-clipboard-check-outline",
      tone: "purple",
    },
      ];
    },
    detailTitle() {
      return ({
      invoices: "Invoice detail",
      returns: "Approved sales returns",
      collections: "Posted collections",
      outstanding: "Current outstanding invoices",
      overdue: "Current overdue invoices",
      })[this.detailKind];
    },
    chartTheme() {
      return this.$vuetify?.theme?.current?.dark ? "dark" : "light";
    },
    salesTargetChartSeries() {
      if (!this.dashboard) return [];
      const series = [];
        series.push({ name: "Sales", data: this.dashboard.trend.map((row) => row.sales === null ? null : Number(row.sales)) });
        series.push({ name: "Approved target", data: this.dashboard.trend.map((row) => row.target === null ? null : Number(row.target)) });
      return series;
    },
    salesTargetChartOptions() {
      return this.lineChartOptions(
        this.dashboard?.trend?.map((row) => row.month) || [],
        ["#1976D2", "#9C27B0"],
      );
    },
    salesCollectionChartSeries() {
      if (!this.dashboard) return [];
      const series = [];
      series.push({ name: "Sales", data: this.dashboard.trend.map((row) => row.sales === null ? null : Number(row.sales)) });
      series.push({ name: "Posted collections", data: this.dashboard.trend.map((row) => Number(row.collections || 0)) });
      return series;
    },
    salesCollectionChartOptions() {
      return this.lineChartOptions(
        this.dashboard?.trend?.map((row) => row.month) || [],
        ["#1976D2", "#2E7D32"],
      );
    },
    pipelineChartSeries() {
      return [{ name: "Pipeline value", data: (this.dashboard?.crm?.pipeline || []).map((row) => Number(row.amount || 0)) }];
    },
    pipelineChartOptions() {
      const rows = this.dashboard?.crm?.pipeline || [];
      return this.barChartOptions(rows.map((row) => row.stage), "#7B1FA2");
    },
    agingChartSeries() {
      return (this.dashboard?.receivables?.aging || []).map((row) => Number(row.amount || 0));
    },
    agingChartOptions() {
      return {
        ...this.donutChartOptions((this.dashboard?.receivables?.aging || []).map((row) => row.label)),
        colors: ["#1976D2", "#2E7D32", "#FB8C00", "#D32F2F", "#7B1FA2", "#757575"],
      };
    },
  },
  watch: {
    detailOpen(open) {
      if (!open) {
        this.detailSequence++;
        this.detailController?.abort();
        this.detailData = null;
      }
    },
  },
  created() {
    this.applyPeriod("month");
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (!["SET_CURRENT_BRANCH", "SET_AUTH_USER", "SET_USER_TOKEN", "UNSET_CURRENT_BRANCH"].includes(mutation.type))
        return;
      this.requestSequence++;
      this.detailSequence++;
      this.requestController?.abort();
      this.detailController?.abort();
      this.dashboard = null;
      this.detailData = null;
      this.detailOpen = false;
      this.approvalOpen = false;
      this.filters.employee_id = null;
      this.filters.currency_id = null;
      clearTimeout(this.scopeTimer);
      this.scopeTimer = setTimeout(() => this.loadDashboard(), 180);
    });
    this.loadDashboard();
  },
  beforeUnmount() {
    this.disposed = true;
    this.requestSequence++;
    this.detailSequence++;
    this.requestController?.abort();
    this.detailController?.abort();
    clearTimeout(this.scopeTimer);
    this.unsubscribe?.();
  },
  methods: {
  lineChartOptions(categories, colors) {
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false }, parentHeightOffset: 0 },
      colors,
      stroke: { curve: "smooth", width: 3 },
      markers: { size: 3 },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { rotate: -35 } },
      yaxis: { labels: { formatter: (value) => this.number(value) } },
      tooltip: { y: { formatter: (value) => this.money(value) } },
      legend: { position: "top" },
      theme: { mode: this.chartTheme },
    };
  },
  barChartOptions(categories, color) {
    return {
      chart: { toolbar: { show: false }, parentHeightOffset: 0 },
      colors: [color],
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { formatter: (value) => this.number(value) } },
      tooltip: { y: { formatter: (value) => this.money(value) } },
      legend: { show: false },
      theme: { mode: this.chartTheme },
    };
  },
  donutChartOptions(labels) {
    return {
      chart: { toolbar: { show: false } },
      labels,
      dataLabels: { enabled: true },
      legend: { position: "bottom" },
      tooltip: { y: { formatter: (value) => this.money(value) } },
      theme: { mode: this.chartTheme },
    };
  },
    localDate(value) {
      return [value.getFullYear(), String(value.getMonth() + 1).padStart(2, "0"), String(value.getDate()).padStart(2, "0")].join("-");
    },
    applyPeriod(value) {
      const now = new Date();
      let start = now;
      let end = now;
      if (value === "custom") return;
      if (value === "month") {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (value === "quarter") {
        const month = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), month, 1);
        end = new Date(now.getFullYear(), month + 3, 0);
      } else if (value === "year") {
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
      }
      this.filters.from_date = this.localDate(start);
      this.filters.to_date = this.localDate(end);
    },
    number(value) {
      if (value === null || value === undefined || value === "") return "—";
      const parsed = Number(value);
      return Number.isFinite(parsed) ? new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2 }).format(parsed) : "—";
    },
    integer(value) { return value === null || value === undefined ? "—" : this.number(value); },
    money(value, currency = this.dashboard?.meta.currency) {
      return value === null || value === undefined ? "—" : (currency ? currency + " " : "") + this.number(value);
    },
    percent(value) { return value === null || value === undefined ? "—" : this.number(value) + "%"; },
    date(value) {
      if (!value) return "—";
      const parsed = new Date(String(value).slice(0, 10) + "T00:00:00");
      return Number.isNaN(parsed.valueOf()) ? "—" : parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    },
    isOverdue(value) { return value && String(value).slice(0, 10) < this.dashboard?.meta.as_of; },
    positionColor(value) {
      return ({
        Achieved: "success",
        "On track": "primary",
        "Behind target": "warning",
        "Behind run rate": "warning",
        "Period ended": "error",
        "Conflicting targets": "error",
        "Currency not mapped": "orange",
        "Target not set": "grey",
      }[value] || "grey");
    },
    trendPercentage(value) {
      const maxVal = Math.max(...(this.dashboard?.trend.map((r) => Math.max(r.sales || 0, r.target || 0, r.collections || 0)) || [1]));
      return maxVal === 0 ? 0 : Math.round((Number(value || 0) / maxVal) * 100);
    },
    pipelineBarWidth(value) {
      const maxVal = Math.max(...(this.dashboard?.crm.pipeline.map((r) => Number(r.amount)) || [1]));
      return maxVal === 0 ? 0 : Math.round((value / maxVal) * 100);
    },
    agingPercentage(amount) {
      const total = this.dashboard?.receivables.total || 0;
      return total === 0 ? 0 : Math.round((amount / total) * 100);
    },
    openCard(key) {
      if (key === "target" || key === "achievement") this.activeTab = "performance";
      else if (key === "backlog") this.activeTab = "operations";
      else if (key === "approvals") this.approvalOpen = true;
      else this.openDetails(key === "sales" ? "invoices" : key);
    },
    requestParams() {
      return { ...this.filters, employee_id: this.filters.employee_id || undefined, currency_id: this.filters.currency_id ?? undefined };
    },
    responsePayload(response) { return response?.data?.data ?? response?.data; },
    errorMessage(exception) {
      const status = exception.response?.status;
      if (status === 403) return "You do not have permission for this dashboard scope. Contact your administrator.";
      if (status === 401) return "Your session has expired. Please sign in again.";
      if (status === 422) return Object.values(exception.response.data.errors || {}).flat().join(" ") || exception.response.data.message;
      return "The dashboard could not be loaded. Please retry or contact your administrator.";
    },
    async loadDashboard() {
      if (this.disposed) return;
      if (!this.filters.from_date || !this.filters.to_date || this.filters.from_date > this.filters.to_date) {
        this.error = "Choose a valid date range (From must not be after To).";
        return;
      }
      const sequence = ++this.requestSequence;
      this.requestController?.abort();
      this.requestController = new AbortController();
      this.$store.commit("SET_LAYOUT_LOADING", {color: 'blue-darken-3'})
      this.error = "";
      this.detailOpen = false;
      this.approvalOpen = false;
      try {
        const params = this.requestParams();
        const response = await Axios.get(this.apiUrl, { params, signal: this.requestController.signal });
        if (sequence !== this.requestSequence || this.disposed) return;
        this.dashboard = this.responsePayload(response);
        this.filters.currency_id = this.dashboard.meta.currency_id;
        this.appliedFilters = { ...params, currency_id: this.dashboard.meta.currency_id };
      } catch (exception) {
        if (sequence !== this.requestSequence || exception.code === "ERR_CANCELED") return;
        this.dashboard = null;
        this.error = this.errorMessage(exception);
      } finally {
        this.$store.commit("UNSET_LAYOUT_LOADING")
      }
    },
    openDetails(kind) {
      this.detailKind = kind;
      this.detailPage = 1;
      this.detailOpen = true;
      this.loadDetails();
    },
    changeDetailKind(kind) {
      this.detailKind = kind;
      this.detailPage = 1;
      this.loadDetails();
    },
    async loadDetails() {
      const sequence = ++this.detailSequence;
      this.detailController?.abort();
      this.detailController = new AbortController();
      this.$store.commit("SET_LAYOUT_LOADING", {color: 'blue-darken-3'})
      this.detailError = "";
      this.detailData = null;
      try {
        const response = await Axios.get(this.apiUrl, {
          params: {
            ...this.appliedFilters,
            detail: this.detailKind,
            page: this.detailPage,
        per_page: 10,
      },
          signal: this.detailController.signal,
        });
        if (sequence === this.detailSequence && this.detailOpen && !this.disposed)
          this.detailData = this.responsePayload(response).detail;
      } catch (exception) {
        if (sequence === this.detailSequence && exception.code !== "ERR_CANCELED")
          this.detailError = this.errorMessage(exception);
      } finally {
        this.$store.commit("UNSET_LAYOUT_LOADING")
      }
    },
  },
};
</script>
